import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getHotDramas, type Drama } from '@/mocks/dramas';

export default function HeroBanner() {
  const hotDramas = getHotDramas();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  const goToSlide = useCallback((index: number) => {
    setCurrentIndex(index);
  }, []);

  useEffect(() => {
    if (hotDramas.length === 0) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % hotDramas.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [hotDramas.length]);

  const currentDrama: Drama | undefined = hotDramas[currentIndex];

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/category/all?search=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  if (hotDramas.length === 0) return null;

  return (
    <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center transition-all duration-700 ease-in-out"
        style={{ backgroundImage: `url(${currentDrama?.bannerImage})` }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 flex justify-center gap-2 pb-6 z-10">
        {hotDramas.map((_, idx) => (
          <button
            key={idx}
            onClick={() => goToSlide(idx)}
            className={`w-2.5 h-2.5 rounded-full transition-all duration-300 cursor-pointer ${
              idx === currentIndex
                ? 'bg-primary-500 w-8'
                : 'bg-white/50 hover:bg-white/80'
            }`}
            aria-label={`切换到第 ${idx + 1} 张`}
          />
        ))}
      </div>

      <div className="absolute inset-0 flex items-center z-10">
        <div className="w-full px-4 md:px-8">
          <div className="max-w-7xl mx-auto w-full">
            <div className="max-w-lg">
              <h1 className="text-3xl md:text-5xl font-heading font-bold text-white mb-3 md:mb-4 leading-tight">
                电视剧全集免费
                <br />
                <span className="text-primary-400">在线观看</span>
              </h1>
              <p className="text-sm md:text-base text-white/80 mb-6 md:mb-8 leading-relaxed">
                热门国产剧韩剧全集一网打尽，高清不卡顿，秒播无需等待
              </p>

              <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400 text-lg"></i>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="搜索你想看的电视剧..."
                    className="w-full h-12 pl-11 pr-4 text-sm rounded-full bg-white/95 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 backdrop-blur-sm"
                  />
                </div>
                <button
                  type="submit"
                  className="h-12 px-8 rounded-full bg-primary-500 hover:bg-primary-600 text-white font-medium text-sm transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  搜索剧集
                </button>
              </form>

              <div className="flex flex-wrap items-center gap-2 mt-4">
                <span className="text-xs text-white/60 whitespace-nowrap">热门搜索：</span>
                {['庆余年', '繁花', '狂飙', '黑色荣耀', '三体'].map((tag) => (
                  <button
                    key={tag}
                    onClick={() => {
                      setSearchQuery(tag);
                      navigate(`/category/all?search=${encodeURIComponent(tag)}`);
                    }}
                    className="px-3 py-1 text-xs rounded-full bg-white/15 hover:bg-white/25 text-white/90 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}